import { Input, Segmented, Space, Switch, Typography } from "antd";
import { AGG_OPTIONS } from "../constants";
import type { TableColumnFormState } from "./tableState";
import { ColorSwatchPicker } from "../shared/ColorSwatchPicker";
import { useReduxMetricDropdown } from "../shared/useReduxMetricDropdown";
import { MetricSelect } from "../shared/MetricSelect";
import {
  CountIncludePicker,
  countSourceFromMetricId,
  defaultCountSpec,
} from "../shared/CountIncludePicker";

const PRESET_COLORS = [
  { color: "#d9534f", title: "Red" },
  { color: "#EF7617", title: "Orange" },
  { color: "#FFC83D", title: "Yellow" },
  { color: "#31B452", title: "Green" },
  { color: "#0000FF", title: "Blue" },
  { color: "#000000", title: "Black" },
  { color: "#cccccc", title: "Grey" },
  { color: "#A9A9A9", title: "Dark Grey" },
];

const { Text } = Typography;

interface TableColumnFormProps {
  col: TableColumnFormState;
  onChange: (patch: Partial<TableColumnFormState>) => void;
}

export function TableColumnForm({ col, onChange }: TableColumnFormProps) {
  const { metricDropdown } = useReduxMetricDropdown({ includeCounts: true });

  return (
    <div className="awm-step-body">
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <MetricSelect
          metricDropdown={metricDropdown}
          value={col.selectedMetricId}
          onChange={(value) => {
            const isGps = value?.startsWith("gps-");
            const source = countSourceFromMetricId(value);
            onChange({
              selectedMetricId: value,
              ...(!isGps && { showAcwr: false }),
              // The include-list belongs to one source, so a metric change replaces it.
              countSpec: source ? defaultCountSpec(source) : undefined,
              // A count has no average or best; the backend accepts only the total.
              // Forced for a count; restored when leaving one, because the author
              // never chose it.
              ...(source
                ? { metricAgg: "sum" as const }
                : col.countSpec ? { metricAgg: "avg" as const } : {}),
            });
          }}
        />
        {col.countSpec && (
          <div className="awm-labeled-row">
            <Text className="awm-labeled-row-label">Counting</Text>
            <CountIncludePicker
              spec={col.countSpec}
              onChange={(countSpec) => onChange({ countSpec })}
            />
          </div>
        )}
        {col.selectedMetricId != null && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text style={{ fontSize: 12 }}>Aggregation</Text>
            {col.countSpec ? (
              <span className="awm-count-fixed-agg">Total</span>
            ) : (
              <Segmented
                size="small"
                options={AGG_OPTIONS.map((a) => ({ label: a.label, value: a.value }))}
                onChange={(value) => onChange({ metricAgg: value as TableColumnFormState["metricAgg"] })}
                value={col.metricAgg}
              />
            )}
          </div>
        )}
        {col.selectedMetricId?.startsWith("gps-") && (
          <div
            style={{
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
              padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8, background: "#fafbfc",
            }}
          >
            <div>
              <Text strong style={{ fontSize: 12 }}>ACWR</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Show Acute:Chronic Workload Ratio instead of the raw metric.
              </Text>
            </div>
            <Switch
              size="small"
              checked={col.showAcwr}
              onChange={(v) => onChange({ showAcwr: v })}
            />
          </div>
        )}
        <div
          style={{
            padding: "8px 10px",
            border: `1px solid ${col.customNameEnabled ? "#1677ff" : "#e0e5eb"}`,
            borderRadius: 8,
            background: col.customNameEnabled ? "#f0f5ff" : "#fafbfc",
            display: "flex", flexDirection: "column", gap: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Override Column Name</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Replace the auto-generated column header.
              </Text>
            </div>
            <Switch
              size="small"
              checked={col.customNameEnabled}
              onChange={(v) => onChange({ customNameEnabled: v, ...(!v && { customName: "" }) })}
            />
          </div>
          {col.customNameEnabled && (
            <Input
              size="small"
              placeholder="e.g. RPE Score"
              value={col.customName}
              onChange={(e) => onChange({ customName: e.target.value })}
              maxLength={80}
            />
          )}
        </div>
        <div
          style={{
            padding: "8px 10px",
            border: `1px solid ${col.enableColorOverride ? "#1677ff" : "#e0e5eb"}`,
            borderRadius: 8,
            background: col.enableColorOverride ? "#f0f5ff" : "#fafbfc",
            display: "flex", flexDirection: "column", gap: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Override Font Color</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Apply a custom color to this column's values.
              </Text>
            </div>
            <Switch
              size="small"
              checked={col.enableColorOverride}
              onChange={(checked) => {
                onChange({ enableColorOverride: checked });
              }}
            />
          </div>
          {col.enableColorOverride && (
            <ColorSwatchPicker
              color={col.colorOverride || ""}
              presetColors={PRESET_COLORS}
              onChange={(hex) => onChange({ colorOverride: hex })}
            />
          )}
        </div>
      </Space>
    </div>
  );
}
